/**
 * useEnabledLanguages — Shared hook for language availability
 *
 * Fetches the platform.enabled_languages setting from the SystemSettings API
 * and exposes the list of language codes the admin has turned on.
 *
 * Guarantees:
 *   - English ('en') is always included regardless of the stored value.
 *   - Returns ALL_LANGUAGE_CODES while loading (avoids flash of missing languages).
 *   - If the setting doesn't exist yet, defaults to English-only so new
 *     deployments start conservatively.
 *
 * The result is cached in-module so multiple consumers don't re-fetch.
 */

import { useEffect, useState } from 'react';
import { getSystemSetting, putSystemSetting, SETTING_KEYS } from '../api/systemSettings';

/** Every language code the platform supports (matches LANG_FLAGS in AvatarMenu). */
export const ALL_LANGUAGE_CODES = ['en', 'ar', 'fr', 'de', 'uk', 'sv', 'ru', 'es'] as const;
export type LanguageCode = (typeof ALL_LANGUAGE_CODES)[number];

/** Default when no setting exists yet — English only. */
const DEFAULT_ENABLED: LanguageCode[] = ['en'];

/* ------------------------------------------------------------------ */
/*  Module-level cache to avoid redundant API calls                     */
/* ------------------------------------------------------------------ */
let _cached: LanguageCode[] | null = null;
let _fetchPromise: Promise<LanguageCode[]> | null = null;

function parseCodes(raw: string): LanguageCode[] {
  const codes = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((c): c is LanguageCode => ALL_LANGUAGE_CODES.includes(c as LanguageCode));
  // Ensure English is always present
  if (!codes.includes('en')) codes.unshift('en');
  return codes;
}

async function fetchEnabledLanguages(): Promise<LanguageCode[]> {
  try {
    const raw = await getSystemSetting(SETTING_KEYS.ENABLED_LANGUAGES);
    if (!raw) return DEFAULT_ENABLED;
    return parseCodes(raw);
  } catch {
    return DEFAULT_ENABLED;
  }
}

function loadEnabledLanguages(): Promise<LanguageCode[]> {
  if (_cached) return Promise.resolve(_cached);
  if (!_fetchPromise) {
    _fetchPromise = fetchEnabledLanguages().then((codes) => {
      _cached = codes;
      _fetchPromise = null;
      return codes;
    });
  }
  return _fetchPromise;
}

/** Invalidate the cache so the next consumer re-fetches from the API. */
export function invalidateEnabledLanguagesCache(): void {
  _cached = null;
  _fetchPromise = null;
}

/**
 * Save a new set of enabled languages to the API and update the cache.
 * Ensures 'en' is always included.
 */
export async function saveEnabledLanguages(codes: LanguageCode[]): Promise<void> {
  // DEV-963: spreading ['en', ...codes] widens to string[]; cast back to the
  // LanguageCode union. 'en' is a valid LanguageCode by construction, and
  // `codes` already carries the union, so the runtime invariant holds.
  const withEn: LanguageCode[] = codes.includes('en') ? codes : (['en', ...codes] as LanguageCode[]);
  const value = withEn.join(',');
  await putSystemSetting(SETTING_KEYS.ENABLED_LANGUAGES, value);
  _cached = withEn;
}

/* ------------------------------------------------------------------ */
/*  React hook                                                          */
/* ------------------------------------------------------------------ */

export function useEnabledLanguages() {
  const [enabled, setEnabled] = useState<LanguageCode[]>(_cached ?? ALL_LANGUAGE_CODES as unknown as LanguageCode[]);
  const [loading, setLoading] = useState(_cached === null);

  useEffect(() => {
    let cancelled = false;
    loadEnabledLanguages().then((codes) => {
      if (!cancelled) {
        setEnabled(codes);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { enabledLanguages: enabled, loading };
}
